import React, { useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { useNavigate } from 'react-router-dom';
import usePreStockInspectionData from './hook/usePreStockInspectionData.js';
import PreStockInspectionTable from './component/PreStockInspectionTable.jsx';
import NewButton from '../../../../components/NewButton/NewButton.jsx';
import DocumentDownloadHeader from '../shared/components/DocumentDownloadHeader.jsx';
import PreStockInspectionFilter from './component/PreStockInspectionFilter';
import PreStockInspectionHistoryDialog from './components/PreStockInspectionHistoryDialog.jsx';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLazyExportPreStockInspectionToExcelQuery } from '../../../../api/endpoints/preStockInspection.js';
import GenericFileExportButton from '../../../../components/common/GenericFileExportButton.jsx';

const PreStockInspectionChecklistPage = () => {
    const navigate = useNavigate();
    const fileKey = 'cfcbed45-17e5-487a-9a99-46d4184b5744';
    const [activeIndex, setActiveIndex] = useState(0);
    const { th, getCurrentLanguageId } = useLanguage();

    // History dialog state
    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [currentHistoryRecord, setCurrentHistoryRecord] = useState(null);

    const STATUS_TAB = [
        { key: 'ALL', label: th('warranty.preStockInspection.tabs.all'), status: null, icon: 'pi pi-list', color: 'blue' },
        { key: 'DRAFT', label: th('warranty.preStockInspection.tabs.draft'), status: 5, icon: 'pi pi-file-edit', color: 'orange' },
        { key: 'SENT', label: th('warranty.preStockInspection.tabs.sent'), status: 10, icon: 'pi pi-send', color: 'green' }
    ];

    const currentStatus = STATUS_TAB[activeIndex].status;

    const { filterFields, handleSearch, handleApplyFilters, handleResetFilters, apiPreStockInspection,tabCounts, isLoading, isFetching, error, refetch, dealerOptions, apiFilters ,currentStatus: currentStatusValue} = usePreStockInspectionData(currentStatus);

    // Custom filter types for GenericTableFilter
    const customFilterTypes = useMemo(
        () => ({
            startDate: 'date',
            endDate: 'date',
            dealerId: 'dropdown'
        }),
        []
    );

    const customFilterOptions = useMemo(() => ({
        dealerId: dealerOptions
    }), [dealerOptions]);

    const exportParams = useMemo(() => ({
        Status: currentStatus,
        startDate: apiFilters.startDate ?? null,
        endDate: apiFilters.endDate ?? null,
        DealerId: apiFilters.DealerId ?? null,
        Vin: apiFilters.Vin ?? null,
        InspectorName: apiFilters.InspectorName ?? null,
        InspectorTitle: apiFilters.InspectorTitle ?? null,
        StockInDate: apiFilters.StockInDate ?? null,
        InspectionDate: apiFilters.InspectionDate ?? null,
        LanguageId: getCurrentLanguageId(),
        compressionType: 0
    }), [currentStatus, apiFilters, getCurrentLanguageId]);

    const handleCreateClaim = () => {
        navigate('/warranty-management/warranty-process-forms/pre-stock-inspection-checklist/create');
    };

    const filteredData = STATUS_TAB[activeIndex].status ? apiPreStockInspection.filter((item) => item.status === STATUS_TAB[activeIndex].status) : apiPreStockInspection;

    const getCurrentTabCount = () => {
        if (!apiPreStockInspection || apiPreStockInspection.length === 0) return 0;
        return filteredData ? filteredData.length : 0;
    };

    const handleShowHistory = (record) => {
        setCurrentHistoryRecord(record);
        setHistoryDialogVisible(true);
    };

    const hideHistoryDialog = () => {
        setHistoryDialogVisible(false);
        setCurrentHistoryRecord(null);
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('warranty.preStockInspection.title')}</h1>
                <div className="flex gap-2">
                    <GenericFileExportButton
                        useExportQuery={useLazyExportPreStockInspectionToExcelQuery}
                        exportParams={exportParams}
                        buttonLabel={th('common.export')}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        defaultFileName="OnStokKontrol"
                    />
                    <NewButton onClick={handleCreateClaim} label={th('warranty.preStockInspection.buttons.new')} />
                </div>
            </div>
            <div className="flex justify-content-between align-items-center mb-5">
                <DocumentDownloadHeader
                    fileKey={fileKey}
                    documentLabelKey="warranty.preStockInspection.download.psiDocument"
                    downloadingMessageKey="warranty.preStockInspection.download.downloading"
                    successMessageKey="warranty.preStockInspection.download.success"
                    errorMessageKey="warranty.preStockInspection.download.error"
                />
            </div>
            <div className="mb-2">
                <PreStockInspectionFilter 
                    filterFields={filterFields} 
                    handleSearch={handleSearch} 
                    handleApplyFilters={handleApplyFilters} 
                    handleResetFilters={handleResetFilters}
                    customFilterTypes={customFilterTypes}
                    customFilterOptions={customFilterOptions}
                />
            </div>
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="pre-stock-inspection-tabs">
                {STATUS_TAB.map((tab, index) => {
                    const count = tab.status === null ? (tabCounts['ALL'] ?? 0) : (tabCounts[tab.status] ?? 0);
                    return (
                        <TabPanel
                            key={tab.key}
                            header={
                                <span className="tab-header">
                                    <i className={`${tab.icon} mr-2`}></i>
                                    {tab.label}
                                    {count > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>{count}</span>}
                                </span>
                            }
                            className="tab-content"
                        >
                            <PreStockInspectionTable preStockInspections={apiPreStockInspection || []} loading={isLoading} handleShowHistory={handleShowHistory} />
                        </TabPanel>
                    );
                })}
            </TabView>

            <PreStockInspectionHistoryDialog dialogVisible={historyDialogVisible} hideDialog={hideHistoryDialog} preStockInspection={currentHistoryRecord} />
        </div>
    );
};

export default PreStockInspectionChecklistPage;
